import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import { Slider } from './ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Target,
  Calendar,
  Zap,
  ArrowUp,
  ArrowDown,
  Minus,
  Link2,
  X,
  Plus,
  Search,
  GitPullRequest,
  CircleDot,
  ExternalLink,
} from 'lucide-react';
import { cn, formatDate } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';

const PRIORITIES = [
  { value: 1, label: 'Urgent', icon: Zap, color: 'text-red-500' },
  { value: 2, label: 'High', icon: ArrowUp, color: 'text-orange-500' },
  { value: 3, label: 'Normal', icon: Minus, color: 'text-blue-500' },
  { value: 4, label: 'Low', icon: ArrowDown, color: 'text-gray-500' },
];

const RESOURCE_TYPES = [
  { value: 'linear_issue', label: 'Linear Issue', icon: CircleDot },
  { value: 'github_pr', label: 'GitHub PR', icon: GitPullRequest },
  { value: 'github_issue', label: 'GitHub Issue', icon: CircleDot },
];

export default function GoalEditor({
  goal = null,
  projectId,
  onSave,
  onCancel,
}) {
  const [title, setTitle] = useState(goal?.title || '');
  const [description, setDescription] = useState(goal?.description || '');
  const [priority, setPriority] = useState(goal?.priority || 3);
  const [progressPercent, setProgressPercent] = useState(goal?.progress_percent || 0);
  const [targetDate, setTargetDate] = useState(
    goal?.target_date ? goal.target_date.split('T')[0] : ''
  );
  const [status, setStatus] = useState(goal?.status || 'active');
  const [visibility, setVisibility] = useState(goal?.visibility || 'team');
  const [saving, setSaving] = useState(false);

  // Linked resources
  const [linkedResources, setLinkedResources] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedResourceType, setSelectedResourceType] = useState('linear_issue');

  // Load linked resources when editing
  useEffect(() => {
    if (goal?.id) {
      loadLinkedResources();
    }
  }, [goal?.id]);

  const loadLinkedResources = async () => {
    if (!goal?.id) return;
    setLoadingResources(true);
    try {
      const response = await api.get(`/projects/${projectId}/goals/${goal.id}`);
      setLinkedResources(response.data.linked_resources || []);
    } catch (error) {
      console.error('Failed to load linked resources:', error);
    } finally {
      setLoadingResources(false);
    }
  };

  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      // Search for resources to link
      const response = await api.get(`/search`, {
        params: {
          q: query,
          source_type: selectedResourceType,
          limit: 10,
        }
      });
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
      // Fallback: show empty results
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [selectedResourceType]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, handleSearch]);

  const handleLinkResource = async (resource) => {
    if (!goal?.id) {
      // For new goals, just add to local state
      setLinkedResources(prev => [...prev, {
        resource_type: selectedResourceType,
        resource_id: resource._key || resource.id,
        resource_title: resource.title || resource.name,
        resource_url: resource.url,
      }]);
      setSearchOpen(false);
      setSearchQuery('');
      return;
    }

    try {
      await api.post(`/projects/${projectId}/goals/${goal.id}/links`, {
        resource_type: selectedResourceType,
        resource_id: resource._key || resource.id,
        resource_title: resource.title || resource.name,
      });
      toast.success('Resource linked');
      loadLinkedResources();
      setSearchOpen(false);
      setSearchQuery('');
    } catch (error) {
      console.error('Failed to link resource:', error);
      toast.error('Failed to link resource');
    }
  };

  const handleUnlinkResource = async (resource) => {
    if (!goal?.id) {
      // For new goals, just remove from local state
      setLinkedResources(prev => prev.filter(r =>
        r.resource_id !== resource.resource_id || r.resource_type !== resource.resource_type
      ));
      return;
    }

    try {
      await api.delete(`/projects/${projectId}/goals/${goal.id}/links`, {
        data: {
          resource_type: resource.resource_type,
          resource_id: resource.resource_id,
        }
      });
      toast.success('Resource unlinked');
      loadLinkedResources();
    } catch (error) {
      console.error('Failed to unlink resource:', error);
      toast.error('Failed to unlink resource');
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim() || null,
        priority,
        progress_percent: progressPercent,
        target_date: targetDate ? new Date(targetDate).toISOString() : null,
        status,
        visibility,
      });
    } finally {
      setSaving(false);
    }
  };

  const getResourceIcon = (type) => {
    const config = RESOURCE_TYPES.find(r => r.value === type);
    return config?.icon || CircleDot;
  };

  return (
    <div className="space-y-4">
      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="title">Goal Title *</Label>
        <Input
          id="title"
          placeholder="What do you want to achieve?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          data-testid="goal-title"
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          placeholder="Describe the goal and success criteria..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="resize-none"
          data-testid="goal-description"
        />
      </div>

      {/* Priority & Status */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Priority</Label>
          <Select value={priority.toString()} onValueChange={(v) => setPriority(parseInt(v))}>
            <SelectTrigger data-testid="goal-priority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORITIES.map(p => {
                const Icon = p.icon;
                return (
                  <SelectItem key={p.value} value={p.value.toString()}>
                    <div className="flex items-center gap-2">
                      <Icon className={cn("h-4 w-4", p.color)} />
                      {p.label}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {goal && (
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="goal-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Progress</Label>
          <span className="text-sm font-medium">{progressPercent}%</span>
        </div>
        <Slider
          value={[progressPercent]}
          onValueChange={([value]) => setProgressPercent(value)}
          max={100}
          step={5}
          className="w-full"
          data-testid="goal-progress"
        />
        <Progress value={progressPercent} className="h-2" />
      </div>

      {/* Target Date */}
      <div className="space-y-2">
        <Label htmlFor="targetDate" className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Target Date
        </Label>
        <Input
          id="targetDate"
          type="date"
          value={targetDate}
          onChange={(e) => setTargetDate(e.target.value)}
          data-testid="goal-target-date"
        />
      </div>

      {/* Visibility */}
      <div className="space-y-2">
        <Label>Visibility</Label>
        <Select value={visibility} onValueChange={setVisibility}>
          <SelectTrigger data-testid="goal-visibility">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="private">Private (only you)</SelectItem>
            <SelectItem value="team">Team (project members)</SelectItem>
            <SelectItem value="org">Organization (everyone)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Linked Resources */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          Linked Resources
        </Label>

        {/* Current linked resources */}
        {linkedResources.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {linkedResources.map((resource, idx) => {
              const ResourceIcon = getResourceIcon(resource.resource_type);
              return (
                <Badge
                  key={`${resource.resource_type}-${resource.resource_id}-${idx}`}
                  variant="secondary"
                  className="flex items-center gap-1 py-1 px-2"
                >
                  <ResourceIcon className="h-3 w-3" />
                  <span className="max-w-[200px] truncate">
                    {resource.resource_title || resource.resource_id}
                  </span>
                  {resource.resource_url && (
                    <a
                      href={resource.resource_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="ml-1 hover:text-primary"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => handleUnlinkResource(resource)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
        )}

        {/* Add resource */}
        <div className="flex gap-2">
          <Select value={selectedResourceType} onValueChange={setSelectedResourceType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RESOURCE_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          <Popover open={searchOpen} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="flex-1">
                <Search className="h-4 w-4 mr-2" />
                Search to link...
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0" align="start">
              <Command>
                <CommandInput
                  placeholder="Search resources..."
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <CommandList>
                  {searching && (
                    <CommandEmpty>Searching...</CommandEmpty>
                  )}
                  {!searching && searchResults.length === 0 && searchQuery.length >= 2 && (
                    <CommandEmpty>No results found.</CommandEmpty>
                  )}
                  {!searching && searchQuery.length < 2 && (
                    <CommandEmpty>Type at least 2 characters to search...</CommandEmpty>
                  )}
                  {searchResults.length > 0 && (
                    <CommandGroup>
                      {searchResults.map((result) => (
                        <CommandItem
                          key={result._key || result.id}
                          onSelect={() => handleLinkResource(result)}
                          className="cursor-pointer"
                        >
                          <div className="flex flex-col gap-1">
                            <span className="font-medium">{result.title || result.name}</span>
                            {result.identifier && (
                              <span className="text-xs text-muted-foreground">{result.identifier}</span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <p className="text-xs text-muted-foreground">
          Link Linear issues, GitHub PRs, or issues to track progress
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!title.trim() || saving}
          data-testid="goal-save"
        >
          {saving ? 'Saving...' : goal ? 'Update Goal' : 'Create Goal'}
        </Button>
      </div>
    </div>
  );
}
